import { Check, CalendarClock } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { ROLE_LABELS } from '@cw/shared';
import { Avatar } from '@/design-system/components/avatar';
import { Panel } from '@/design-system/components/panel';
import { Skeleton } from '@/design-system/components/skeleton';
import { ErrorState } from '@/design-system/components/states';
import { DayRail, type DayRailItem } from '@/design-system/time/day-rail';
import { addDays, startOfDay, useCalendar } from '@/lib/calendar';
import { useMemo } from 'react';
import { useTasks } from '@/lib/tasks';
import { useOpenTask } from '@/features/tasks/task-drawer';
import { isDoneTask } from '@/features/tasks/task-row';
import { TaskDue } from '@/features/tasks/due';
import { useNow } from '@/design-system/time/use-now';
import { useInvitations, useMembers } from '@/lib/members';
import { useProjects } from '@/lib/projects';
import { DueLabel } from '@/features/projects/project-card';
import { useCan, useMe } from '@/lib/session';
import { capitalizeFirst, cn } from '@/lib/utils';
import { Pulse, RecentActivity } from './pulse';

function greeting(h: number) {
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
}

/**
 * Home (SPEC §7): o pulso do estúdio (atrasos, entregas da semana, aprovações, a receber),
 * a régua do dia com a linha do agora, minhas tarefas, projetos, equipe e atividade recente.
 */
export function HomePage() {
  const me = useMe();
  const now = useNow();
  const day = now.toLocaleDateString('pt-BR', { day: '2-digit' });
  const dateLine = capitalizeFirst(
    `${now.toLocaleDateString('pt-BR', { weekday: 'long' })}, ${now.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`,
  );
  const firstName = me.user.name.split(' ')[0];
  const openTask = useOpenTask();
  const today = useTasks({ assignee: 'me', due: 'today' });
  // Entregas de hoje na régua: o bloco termina no horário do prazo e dura a estimativa (ou 1 h).
  const railItems: DayRailItem[] = (today.data ?? []).map((t) => {
    const due = new Date(t.internalDueAt!);
    const end = due.getHours() * 60 + due.getMinutes();
    return {
      id: t.id,
      start: Math.max(0, end - 60),
      end,
      title: t.title,
      subtitle: t.project?.name ?? t.client?.name,
      color: `var(--color-label-${t.project?.color ?? t.status.color})`,
      done: isDoneTask(t),
      onOpen: () => openTask(t.id),
    };
  });
  // Reuniões e compromissos de hoje em que a pessoa está.
  const dayStart = useMemo(() => startOfDay(new Date()), []);
  const meetings = useCalendar(dayStart, addDays(dayStart, 1), {
    due: 'internal',
    userId: 'me',
    kinds: 'meeting,review,appointment,publication,other',
  });
  const navigate = useNavigate();
  for (const e of meetings.data ?? []) {
    if (e.allDay) continue;
    const s = new Date(e.start);
    const en = new Date(e.end);
    railItems.push({
      id: e.key,
      start: s.getHours() * 60 + s.getMinutes(),
      end: Math.max(s.getHours() * 60 + s.getMinutes() + 15, en.getHours() * 60 + en.getMinutes()),
      title: e.title,
      subtitle: e.project?.name ?? e.location ?? undefined,
      color: `var(--color-label-${e.color})`,
      onOpen: () => navigate(`/calendar?view=day`),
    });
  }

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 pt-4 pb-10 md:px-8 md:pt-6">
      <header className="mb-8 flex items-end gap-5 md:mb-10">
        <span className="tabular text-[88px] leading-[0.8] font-semibold tracking-[-0.07em] md:text-[120px]">
          {day}
        </span>
        <div className="pb-1">
          <p className="text-[15px] text-fg-muted">{dateLine}</p>
          <h1 className="mt-1 text-[24px] leading-tight font-semibold tracking-[-0.02em] md:text-[28px]">
            {greeting(now.getHours())}, {firstName}.
          </h1>
        </div>
      </header>

      <div className="mb-6">
        <Pulse />
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex flex-col gap-6">
          <Panel title="Seu dia" bodyClassName="pt-6 pb-5 pr-5">
            <DayRail
              items={railItems}
              empty={
                <div className="flex items-start gap-3 rounded-panel bg-stage p-4 shadow-[inset_0_0_0_1px_var(--color-line)]">
                  <CalendarClock className="mt-0.5 size-5 shrink-0 text-fg-subtle" />
                  <div>
                    <p className="text-[14px] font-medium">Nada agendado para hoje</p>
                    <p className="mt-1 max-w-md text-[13px] text-fg-muted">
                      Reuniões e entregas de hoje aparecem nesta régua, com a linha do agora correndo por ela.
                    </p>
                  </div>
                </div>
              }
            />
          </Panel>
          <MyTasks />
          <RecentActivity />
        </div>

        <div className="flex flex-col gap-6">
          <GettingStarted />
          <MyProjects />
          {useCan('members:read') && <TeamGlance />}
        </div>
      </div>
    </div>
  );
}

/** Checklist calculada a partir de dados reais. Some quando tudo estiver feito. */
function GettingStarted() {
  const me = useMe();
  const canInvite = useCan('members:invite');
  const members = useMembers();
  const invitations = useInvitations();
  const hasTeam = (members.data?.length ?? 0) > 1 || (invitations.data?.length ?? 0) > 0;

  const steps = [
    { done: Boolean(me.user.title), label: 'Informe seu cargo', to: '/settings/account', show: true },
    { done: hasTeam, label: 'Convide sua equipe', to: '/settings/members?invite=1', show: canInvite },
  ].filter((s) => s.show);

  if (members.isPending && canInvite) return <Skeleton className="h-40" />;
  if (steps.every((s) => s.done)) return null;
  const doneCount = steps.filter((s) => s.done).length;

  return (
    <Panel
      title="Primeiros passos"
      action={
        <span className="tabular text-[12.5px] text-fg-subtle">
          {doneCount} de {steps.length}
        </span>
      }
    >
      <ul className="flex flex-col gap-1">
        {steps.map((s) => (
          <li key={s.label}>
            <Link
              to={s.to}
              className="flex h-10 items-center gap-3 rounded-control px-2 -mx-2 text-[13.5px] transition-colors hover:bg-hover"
            >
              <span
                className={cn(
                  'flex size-5 shrink-0 items-center justify-center rounded-full',
                  s.done ? 'bg-signal text-signal-fg' : 'shadow-[inset_0_0_0_1.5px_var(--color-line-strong)]',
                )}
              >
                {s.done && <Check className="size-3" strokeWidth={3} />}
              </span>
              <span className={cn(s.done && 'text-fg-muted line-through')}>{s.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function TeamGlance() {
  const members = useMembers();
  return (
    <Panel
      title="Equipe"
      action={
        <Link to="/team" className="text-[12.5px] text-fg-muted hover:text-fg">
          Ver todos
        </Link>
      }
    >
      {members.isPending ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-9" />
          ))}
        </div>
      ) : members.isError ? (
        <ErrorState className="py-6" error={members.error} onRetry={() => members.refetch()} />
      ) : (
        <ul className="flex flex-col gap-3">
          {members.data
            .filter((m) => m.status === 'active')
            .slice(0, 6)
            .map((m) => (
              <li key={m.userId} className="flex items-center gap-3">
                <Avatar name={m.name} src={m.avatarUrl} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-medium">{m.name}</p>
                  <p className="truncate text-[12px] text-fg-muted">{m.title ?? ROLE_LABELS[m.role]}</p>
                </div>
              </li>
            ))}
        </ul>
      )}
    </Panel>
  );
}

function MyProjects() {
  const can = useCan('projects:read');
  const projects = useProjects({ mine: true, status: 'open' });
  if (!can) return null;
  return (
    <Panel
      title="Meus projetos"
      action={
        <Link to="/projects/kanban?mine=1" className="text-[12.5px] text-fg-muted hover:text-fg">
          Ver todos
        </Link>
      }
    >
      {projects.isPending ? (
        <Skeleton className="h-24" />
      ) : !projects.data?.length ? (
        <p className="text-[13.5px] text-fg-muted">Você ainda não está em nenhum projeto em andamento.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {projects.data.slice(0, 6).map((p) => (
            <li key={p.id}>
              <Link
                to={`/projects/p/${p.id}`}
                className="-mx-2 flex items-center gap-3 rounded-control px-2 py-2 hover:bg-hover"
              >
                <span
                  className="h-8 w-1 shrink-0 rounded-full"
                  style={{ background: `var(--color-label-${p.color})` }}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-medium">{p.name}</p>
                  <p className="truncate text-[12px] text-fg-muted">{p.client?.name ?? 'Interno'}</p>
                </div>
                <DueLabel date={p.dueDate} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** Minhas tarefas abertas: atrasadas, de hoje e da semana (SPEC §7). */
function MyTasks() {
  const can = useCan('tasks:read');
  const openTask = useOpenTask();
  const overdue = useTasks({ assignee: 'me', due: 'overdue', open: true }, can);
  const week = useTasks({ assignee: 'me', due: 'week', open: true }, can);
  if (!can) return null;
  const groups = [
    { label: 'Atrasadas', items: overdue.data ?? [] },
    { label: 'Próximos 7 dias', items: week.data ?? [] },
  ].filter((g) => g.items.length);
  return (
    <Panel title="Minhas tarefas">
      {overdue.isPending || week.isPending ? (
        <Skeleton className="h-24" />
      ) : groups.length === 0 ? (
        <p className="text-[13.5px] text-fg-muted">Nada pendente com prazo nos próximos dias.</p>
      ) : (
        <div className="flex flex-col gap-5">
          {groups.map((g) => (
            <div key={g.label}>
              <p
                className={cn(
                  'mb-2 text-[12.5px]',
                  g.label === 'Atrasadas' ? 'text-danger' : 'text-fg-subtle',
                )}
              >
                {g.label} <span className="tabular">{g.items.length}</span>
              </p>
              <ul className="flex flex-col">
                {g.items.slice(0, 8).map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => openTask(t.id)}
                      className="-mx-2 flex w-[calc(100%+16px)] items-center gap-3 rounded-control px-2 py-2 text-left hover:bg-hover"
                    >
                      <span
                        className="size-2 shrink-0 rounded-full"
                        style={{ background: `var(--color-label-${t.status.color})` }}
                        title={t.status.name}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px]">{t.title}</span>
                        <span className="block truncate text-[12px] text-fg-subtle">
                          {t.project?.name ?? t.client?.name ?? 'Sem projeto'}
                        </span>
                      </span>
                      <TaskDue iso={t.internalDueAt} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
